import React, { useCallback, useEffect, useState } from 'react';
import { Text } from '@/components/typography';

interface Props {
	defaultValue?: number | string;
	max: number | string;
	maxLabel?: JSX.Element | string;
	min: number | string;
	minLabel?: JSX.Element | string;
	name: string;
	onBlur?: (newValue: number) => void;
	onChange?: (newValue: number) => void;
	onRef?: () => void;
	step: number | number[];
	value?: number | string;
}

function getMaxAlignedToStep(max: number, step: number | number[], maxLeftover: number): number {
	if (Array.isArray(step)) {
		return step.length - 1;
	}

	return max + (maxLeftover && step - maxLeftover);
}

function getMinAlignedToStep(min: number, step: number | number[]): number {
	if (Array.isArray(step)) {
		return 0;
	}

	return min - (min % step);
}

function getProgressWidth(
	stepAlignedValue: number,
	minAlignedToStep: number,
	maxAlignedToStep: number,
	hasNoSelectableOptions: boolean
): number {
	if (hasNoSelectableOptions) {
		return 100;
	}
	return ((stepAlignedValue - minAlignedToStep) / (maxAlignedToStep - minAlignedToStep)) * 100;
}

export function RangeSlider(props: Props): JSX.Element {
	const { defaultValue, minLabel, maxLabel, onBlur, onChange, onRef, ...inputProps } = props;

	const controlledComponent = 'value' in props;
	const value = Number(props.value);
	const multiSteps = Array.isArray(inputProps.step) ? inputProps.step : null;
	const [privateValue, setPrivateValue] = useState<number>(
		Number(
			controlledComponent
				? multiSteps
					? multiSteps.findIndex((step) => step === value)
					: value
				: defaultValue
		)
	);

	useEffect(() => {
		if (controlledComponent) {
			if (multiSteps) {
				setPrivateValue(multiSteps.findIndex((step) => step === value));
			} else {
				setPrivateValue(value);
			}
		}
	}, [controlledComponent, multiSteps, value]);

	const max = Number(inputProps.max);
	const min = Number(inputProps.min);
	const step = Number(inputProps.step);

	const maxLeftover = multiSteps ? 0 : max % step;

	const maxAlignedToStep = multiSteps
		? multiSteps.length - 1
		: getMaxAlignedToStep(max, step, maxLeftover);
	const minAlignedToStep = multiSteps ? 0 : getMinAlignedToStep(min, step);

	const alignedValue = multiSteps
		? privateValue
		: privateValue === max
			? maxAlignedToStep
			: privateValue === min
				? minAlignedToStep
				: privateValue;

	const stepAlignedValue = multiSteps ? alignedValue : Math.round(alignedValue / step) * step;

	const hasNoSelectableOptions =
		stepAlignedValue === minAlignedToStep && stepAlignedValue === maxAlignedToStep;

	const progressWidth = getProgressWidth(
		stepAlignedValue,
		minAlignedToStep,
		maxAlignedToStep,
		hasNoSelectableOptions
	);

	const correctValue = useCallback(
		(val: number) => {
			if (multiSteps) {
				return multiSteps[Number(val)];
			}
			return Math.min(max, Math.max(min, val));
		},
		[multiSteps, max, min]
	);

	const handleBlur = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => onBlur?.(correctValue(parseInt(e.target.value))),
		[correctValue, onBlur]
	);
	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const rawValue = Number(e.target.value);
			onChange?.(correctValue(rawValue));
		},
		[correctValue, onChange]
	);

	return (
		<div>
			<div className="shadow-slider h-6 w-full rounded-xl bg-white p-2">
				<div className="relative h-full">
					<div
						className="absolute top-0 left-0 z-10 h-full rounded-xl bg-blue-500"
						style={{ width: `${Math.min(progressWidth, 100)}%` }}
					/>
					<input
						{...inputProps}
						max={maxAlignedToStep}
						min={minAlignedToStep}
						onBlur={handleBlur}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							const rawValue = Number(event.target.value);
							setPrivateValue(rawValue);
							handleChange(event);
						}}
						ref={onRef}
						type="range"
						value={alignedValue}
						step={multiSteps ? 1 : (inputProps.step as number)}
						aria-label={inputProps.name}
						aria-valuemin={minAlignedToStep}
						aria-valuemax={maxAlignedToStep}
						aria-valuenow={stepAlignedValue}
						aria-valuetext={
							multiSteps ? String(multiSteps[privateValue]) : String(stepAlignedValue)
						}
						className="rangeSlider absolute right-0 left-0"
					/>
				</div>
			</div>
			{minLabel && maxLabel && (
				<Text as="div" variant="p3" className="mt-3 flex items-center justify-between">
					<p>{minLabel}</p>
					<p>{maxLabel}</p>
				</Text>
			)}
		</div>
	);
}
